import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, X } from 'lucide-react';
import {
  SALLE_STATUTS,
  SALLE_TYPES,
  createSalleSchema,
  type CreateSalleInput,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal CRUD salle (US-BAT-04). Champs réservation inclus
 * (`est_reservable`, `duree_max_reservation`, `preavis_requis`,
 * `validation_requise`). Champs Form Builder déférés.
 */
export function SalleFormModal({
  salleId,
  etageId,
  onClose,
  onSaved,
}: {
  salleId?: string;
  etageId?: string;
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = !!salleId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateSalleInput>({
    // Cast nécessaire car la version transformée du schéma (avec defaults)
    // produit un type Resolver dont les `names` sont plus larges que la
    // version inférée par `useForm<T>`. Fonctionnement runtime identique.
    resolver: zodResolver(createSalleSchema) as unknown as Resolver<CreateSalleInput>,
    defaultValues: {
      etage_id: etageId ?? '',
      type: 'bureau',
      statut: 'disponible',
      est_reservable: false,
      validation_requise: false,
    },
  });

  const estReservable = watch('est_reservable');

  useEffect(() => {
    if (!salleId) {
      // En création : auto-suggest du code via le préfixe tenant
      // `numbering.salle_prefix`. L'utilisateur peut le modifier librement.
      api
        .get<{ data: { code: string } }>('/numbering/suggest?kind=salle')
        .then((res) => reset((prev) => ({ ...prev, code: res.data.data.code }) as typeof prev))
        .catch(() => {
          // Silencieux.
        });
      return;
    }
    (async () => {
      try {
        const res = await api.get<{ data: CreateSalleInput }>(`/salles/${salleId}`);
        reset({
          etage_id: res.data.data.etage_id,
          code: res.data.data.code,
          numero_porte: res.data.data.numero_porte ?? '',
          nom: res.data.data.nom,
          type: res.data.data.type,
          surface: res.data.data.surface ?? undefined,
          statut: res.data.data.statut,
          service_id: res.data.data.service_id ?? undefined,
          responsable_id: res.data.data.responsable_id ?? undefined,
          est_reservable: res.data.data.est_reservable,
          duree_max_reservation: res.data.data.duree_max_reservation ?? undefined,
          preavis_requis: res.data.data.preavis_requis ?? undefined,
          validation_requise: res.data.data.validation_requise,
        });
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [salleId, reset]);

  async function onSubmit(values: CreateSalleInput) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        numero_porte: values.numero_porte || null,
        surface: values.surface || null,
        duree_max_reservation: values.duree_max_reservation || null,
        preavis_requis: values.preavis_requis || null,
      };
      if (isEdit) {
        await api.patch(`/salles/${salleId}`, payload);
      } else {
        await api.post('/salles', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!salleId) return;
    if (!confirm('Supprimer cette salle ? (soft delete)')) return;
    setSaving(true);
    try {
      await api.delete(`/salles/${salleId}`);
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Modifier la salle' : 'Nouvelle salle'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="salle-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <input type="hidden" {...register('etage_id')} />
                <section>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase mb-2">Identité</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Code *" error={errors.code?.message}>
                      <input {...register('code')} placeholder="S-001" className={inputClass} />
                    </Field>
                    <Field label="N° de porte" error={errors.numero_porte?.message}>
                      <input
                        {...register('numero_porte')}
                        placeholder="103"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Nom *" error={errors.nom?.message} className="md:col-span-2">
                      <input {...register('nom')} className={inputClass} />
                    </Field>
                    <Field label="Type *" error={errors.type?.message}>
                      <select {...register('type')} className={inputClass}>
                        {SALLE_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Statut *" error={errors.statut?.message}>
                      <select {...register('statut')} className={inputClass}>
                        {SALLE_STATUTS.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Surface (m²)" error={errors.surface?.message}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        {...register('surface', {
                          setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                        })}
                        className={inputClass}
                      />
                    </Field>
                  </div>
                </section>

                <section className="pt-3 border-t border-gray-200">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase mb-2">
                    Paramètres de réservation
                  </h3>
                  <label className="flex items-center cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      {...register('est_reservable')}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Salle réservable</span>
                  </label>
                  {estReservable && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                      <Field
                        label="Durée max (minutes)"
                        error={errors.duree_max_reservation?.message}
                      >
                        <input
                          type="number"
                          min="0"
                          {...register('duree_max_reservation', { valueAsNumber: true })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Préavis requis (heures)" error={errors.preavis_requis?.message}>
                        <input
                          type="number"
                          min="0"
                          {...register('preavis_requis', { valueAsNumber: true })}
                          className={inputClass}
                        />
                      </Field>
                      <label className="flex items-center cursor-pointer col-span-2">
                        <input
                          type="checkbox"
                          {...register('validation_requise')}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Validation requise par un responsable
                        </span>
                      </label>
                    </div>
                  )}
                </section>
              </form>
            )}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  disabled={saving}
                >
                  Supprimer
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="salle-form"
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
